import axios from 'axios'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'
import { config } from '../../function/config'
import SuperAdminHeader from './SuperAdminHeader'

export default function AddBranches() {
  const navigate = useNavigate()
  const token = localStorage.getItem('token')

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [existingAdmins, setExistingAdmins] = useState([])
  const [existingBranches, setExistingBranches] = useState([])
  const [existingStaffs, setExistingStaffs] = useState([])

  const [selectedFile, setSelectedFile] = useState(null)
  const [openUploadModal, setOpenUploadModal] = useState(false)

  const [formData, setFormData] = useState({
    branch_name: '',
    address: '',
    phone: '',
    email: ''
  })

  useEffect(() => {
    const fetchExistingData = async () => {
      try {
        const headers = {
          Authorization: `Token ${token}`
        }

        const [
          adminsResponse,
          branchesResponse,
          staffsResponse
        ] = await Promise.all([
          axios.get(
            `${config.baseurl}get_all_admins/`,
            { headers }
          ),

          axios.get(
            `${config.baseurl}get_all_branches/`,
            { headers }
          ),

          axios.get(
            `${config.baseurl}get_all_staffs/`,
            { headers }
          )
        ])

        const admins = Array.isArray(adminsResponse.data)
          ? adminsResponse.data
          : adminsResponse.data?.data || []

        const branches = Array.isArray(branchesResponse.data)
          ? branchesResponse.data
          : branchesResponse.data?.data || []

        const staffs = Array.isArray(staffsResponse.data)
          ? staffsResponse.data
          : staffsResponse.data?.data || []

        console.log('Existing Admins:', admins)
        console.log('Existing Branches:', branches)
        console.log('Existing Staffs:', staffs)

        setExistingAdmins(admins)
        setExistingBranches(branches)
        setExistingStaffs(staffs)

      } catch (error) {
        console.error(
          'Failed to fetch existing data:',
          error.response?.data || error
        )
      }
    }

    if (token) {
      fetchExistingData()
    }
  }, [token])

  const handleChange = (e) => {
    const { name, value } = e.target

    let newValue = value
    let error = ''

    // ============================================================
    // BRANCH NAME - LIVE VALIDATION
    // ============================================================
    if (name === 'branch_name') {
      newValue = value

      const enteredName = newValue.trim().toLowerCase()

      if (!enteredName) {
        error = 'Branch Name is required'
      } else if (enteredName.length < 2) {
        error = 'Branch Name must contain at least 2 characters'
      } else if (!/^[a-zA-Z0-9\s&().'-]+$/.test(enteredName)) {
        error =
          'Branch Name can contain only letters, numbers, spaces and basic special characters'
      } else {

        // Branch
        const branchExists = existingBranches.some(
          (branch) =>
            String(branch.branch_name || '')
              .trim()
              .toLowerCase() === enteredName
        )

        if (branchExists) {
          error = 'This name already exists as a Branch'
        }

        // Admin
        if (!error) {
          const adminExists = existingAdmins.some(
            (admin) =>
              String(admin.admin_name || '')
                .trim()
                .toLowerCase() === enteredName
          )

          if (adminExists) {
            error = 'This name already exists as an Admin'
          }
        }

        // Staff
        if (!error) {
          const staffExists = existingStaffs.some((staff) => {
            const firstName = String(
              staff.first_name || ''
            ).trim()

            const lastName = String(
              staff.last_name || ''
            ).trim()

            const staffName =
              `${firstName} ${lastName}`
                .trim()
                .toLowerCase()

            return staffName === enteredName
          })

          if (staffExists) {
            error = 'This name already exists as a Staff'
          }
        }
      }
    }

    // ============================================================
    // ADDRESS - LIVE VALIDATION
    // ============================================================
    else if (name === 'address') {
      newValue = value

      if (!newValue.trim()) {
        error = 'Address is required'
      }
    }

    // ============================================================
    // PHONE - LIVE VALIDATION
    // ============================================================
    else if (name === 'phone') {
      newValue = value
        .replace(/\D/g, '')
        .slice(0, 10)

      if (!newValue) {
        error = 'Phone Number is required'
      }

      // First digit
      else if (!/^[6-9]/.test(newValue)) {
        error =
          'Phone number must start with 6, 7, 8 or 9'
      }

      // Length
      else if (newValue.length < 10) {
        error =
          'Phone number must contain exactly 10 digits'
      }

      // Duplicate check only after 10 digits
      else {

        // Branch
        const branchPhoneExists =
          existingBranches.some(
            (branch) =>
              String(branch.phone || '').trim() ===
              newValue
          )

        if (branchPhoneExists) {
          error =
            'This phone number already exists in a Branch'
        }

        // Admin
        if (!error) {
          const adminPhoneExists =
            existingAdmins.some(
              (admin) =>
                String(admin.phone || '').trim() ===
                newValue
            )

          if (adminPhoneExists) {
            error =
              'This phone number already exists for an Admin'
          }
        }

        // Staff
        if (!error) {
          const staffPhoneExists =
            existingStaffs.some(
              (staff) =>
                String(staff.phone || '').trim() ===
                newValue
            )

          if (staffPhoneExists) {
            error =
              'This phone number already exists for a Staff'
          }
        }
      }
    }

    // ============================================================
    // EMAIL - LIVE VALIDATION
    // ============================================================
    else if (name === 'email') {
      newValue = value.replace(/\s/g, '')

      if (!newValue) {
        error = 'Email Address is required'
      }

      else if (!newValue.includes('@')) {
        error = 'Missing @ symbol'
      }

      else {
        const emailParts = newValue.split('@')

        if (emailParts.length > 2) {
          error =
            'Email address can contain only one @ symbol'
        }

        else {
          const username = emailParts[0]
          const domain = emailParts[1]

          if (!username) {
            error =
              'Username is missing before "@"'
          }

          else if (!domain) {
            error = 'Domain name is missing'
          }

          else if (!domain.includes('.')) {
            error =
              'Missing "dot(.)" after domain name'
          }

          else {
            const allowedDomains = [
              'gmail.com',
              'yahoo.com',
              'hotmail.com',
              'outlook.com'
            ]

            if (
              !allowedDomains.includes(
                domain.toLowerCase()
              )
            ) {
              error =
                'Only gmail.com, yahoo.com, hotmail.com and outlook.com are allowed'
            }

            // Proper email format
            else if (
              !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                newValue
              )
            ) {
              error =
                'Please enter a valid email address'
            }

            else {
              const enteredEmail =
                newValue.trim().toLowerCase()

              // Branch
              const branchEmailExists =
                existingBranches.some(
                  (branch) =>
                    String(branch.email || '')
                      .trim()
                      .toLowerCase() ===
                    enteredEmail
                )

              if (branchEmailExists) {
                error =
                  'This email already exists in a Branch'
              }

              // Admin
              if (!error) {
                const adminEmailExists =
                  existingAdmins.some(
                    (admin) =>
                      String(admin.email || '')
                        .trim()
                        .toLowerCase() ===
                      enteredEmail
                  )

                if (adminEmailExists) {
                  error =
                    'This email already exists for an Admin'
                }
              }

              // Staff
              if (!error) {
                const staffEmailExists =
                  existingStaffs.some(
                    (staff) =>
                      String(staff.email || '')
                        .trim()
                        .toLowerCase() ===
                      enteredEmail
                  )

                if (staffEmailExists) {
                  error =
                    'This email already exists for a Staff'
                }
              }
            }
          }
        }
      }
    }

    // ============================================================
    // UPDATE FORM DATA
    // ============================================================
    setFormData((prev) => ({
      ...prev,
      [name]: newValue
    }))

    // ============================================================
    // UPDATE ERROR LIVE
    // ============================================================
    setErrors((prev) => ({
      ...prev,
      [name]: error
    }))
  }
  
  // Validation
  const validateForm = (data) => {
    const newErrors = {}

    // ============================================================
    // BRANCH NAME
    // ============================================================

    const branchName = String(data.branch_name || '').trim()

    if (!branchName) {
      newErrors.branch_name = 'Branch Name is required'
    } else if (branchName.length < 2) {
      newErrors.branch_name = 'Branch Name must contain at least 2 characters'
    } else if (!/^[a-zA-Z0-9\s&().'-]+$/.test(branchName)) {
      newErrors.branch_name =
        'Branch Name can contain only letters, numbers, spaces and basic special characters'
    }


    // ============================================================
    // ADDRESS
    // ============================================================

    const address = String(data.address || '').trim()

    if (!address) {
      newErrors.address = 'Address is required'
    }


    // ============================================================
    // EMAIL
    // ============================================================

    const email = String(data.email || '')
      .trim()
      .toLowerCase()

    if (!email) {
      newErrors.email = 'Email Address is required'
    } else if (!email.includes('@')) {
      newErrors.email = 'Missing @ symbol'
    } else {
      const emailParts = email.split('@')

      if (emailParts.length > 2) {
        newErrors.email =
          'Email address can contain only one @ symbol'
      } else {
        const username = emailParts[0]
        const domain = emailParts[1]

        if (!username) {
          newErrors.email =
            'Username is missing before "@"'
        } else if (!domain) {
          newErrors.email =
            'Domain name is missing'
        } else if (!domain.includes('.')) {
          newErrors.email =
            'Missing "dot(.)" after domain name'
        } else {
          const allowedDomains = [
            'gmail.com',
            'yahoo.com',
            'hotmail.com',
            'outlook.com'
          ]

          if (!allowedDomains.includes(domain)) {
            newErrors.email =
              'Only gmail.com, yahoo.com, hotmail.com and outlook.com are allowed'
          } else if (
            !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
          ) {
            newErrors.email =
              'Please enter a valid email address'
          }
        }
      }
    }


    // ============================================================
    // PHONE
    // ============================================================

    const phone = String(data.phone || '').trim()

    if (!phone) {
      newErrors.phone = 'Phone Number is required'
    } else if (!/^\d+$/.test(phone)) {
      newErrors.phone =
        'Phone number must contain only digits'
    } else if (!/^[6-9]\d{9}$/.test(phone)) {
      newErrors.phone =
        'Phone number must start with 6, 7, 8 or 9 and contain exactly 10 digits'
    }


    return newErrors
  }

  // Download Excel Template
  const handleExcel = () => {
    const excelData = [
      {
        branch_name: '',
        address: '',
        phone: '',
        email: ''
      }
    ]

    const worksheet = XLSX.utils.json_to_sheet(excelData)

    worksheet['!cols'] = [
      { wch: 25 },
      { wch: 45 },
      { wch: 18 },
      { wch: 30 }
    ]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet( workbook, worksheet, 'Branches')
    XLSX.writeFile( workbook, 'Add Branches.xlsx')
    toast.success('Excel file downloaded successfully')
  }

  // Handle Excel File Selection
  const handleFileChange = (e) => {
    const file = e.target.files?.[0]

    if (!file) {
      return
    }

    const allowedExtensions = ['.xlsx', '.xls']
    const fileName = file.name.toLowerCase()
    const isValidFile = allowedExtensions.some((extension) => fileName.endsWith(extension))

    if (!isValidFile) {
      toast.error('Please select an Excel file (.xlsx or .xls)')
      e.target.value = ''
      setSelectedFile(null)
      return
    }

    setSelectedFile(file)
  }

// Upload Excel File
  const handleUpload = async (e) => {
    e.preventDefault()

    if (!selectedFile) {
      toast.error('Please select an Excel file')
      return
    }

    try {
      setUploading(true)

      // ============================================================
      // READ EXCEL FILE
      // ============================================================

      const arrayBuffer = await selectedFile.arrayBuffer()

      const workbook = XLSX.read(arrayBuffer, {
        type: 'array'
      })

      const sheetName = workbook.SheetNames[0]

      if (!sheetName) {
        toast.error('No worksheet found in Excel file')
        return
      }

      const worksheet = workbook.Sheets[sheetName]

      const excelData = XLSX.utils.sheet_to_json(
        worksheet,
        {
          defval: ''
        }
      )

      // ============================================================
      // CHECK EXCEL DATA
      // ============================================================

      if (!excelData.length) {
        toast.error('Excel file is empty')
        return
      }

      // ============================================================
      // FETCH LATEST BRANCH DATA
      // ============================================================

      const headers = {
        Authorization: `Token ${token}`
      }

      const [
        branchesResponse,
        adminsResponse,
        staffsResponse
      ] = await Promise.all([
        axios.get(
          `${config.baseurl}get_all_branches/`,
          { headers }
        ),

        axios.get(
          `${config.baseurl}get_all_admins/`,
          { headers }
        ),

        axios.get(
          `${config.baseurl}get_all_staffs/`,
          { headers }
        )
      ])

      const existingBranches =
        branchesResponse.data?.data || []

      const existingAdmins =
        adminsResponse.data?.data || []

      const existingStaffs =
        staffsResponse.data?.data || []

      // ============================================================
      // EXISTING BRANCH NAMES
      // ============================================================

      const existingBranchNames = new Set(
        existingBranches
          .filter((branch) => branch.branch_name)
          .map((branch) =>
            String(branch.branch_name)
              .trim()
              .toLowerCase()
          )
      )

      // ============================================================
      // EXISTING BRANCH EMAILS
      // ============================================================

      const existingEmails = new Set(
        existingBranches
          .filter((branch) => branch.email)
          .map((branch) =>
            String(branch.email)
              .trim()
              .toLowerCase()
          )
      )

      // ============================================================
      // EXISTING BRANCH PHONES
      // ============================================================

      const existingPhones = new Set(
        existingBranches
          .filter((branch) => branch.phone)
          .map((branch) =>
            String(branch.phone)
              .trim()
          )
      )

      // ============================================================
      // EXISTING ADMIN EMAILS
      // ============================================================

      const existingAdminEmails = new Set(
        existingAdmins
          .filter((admin) => admin.email)
          .map((admin) =>
            String(admin.email)
              .trim()
              .toLowerCase()
          )
      )

      // ============================================================
      // EXISTING ADMIN PHONES
      // ============================================================

      const existingAdminPhones = new Set(
        existingAdmins
          .filter((admin) => admin.phone)
          .map((admin) =>
            String(admin.phone)
              .trim()
          )
      )

      // ============================================================
      // EXISTING STAFF EMAILS
      // ============================================================

      const existingStaffEmails = new Set(
        existingStaffs
          .filter((staff) => staff.email)
          .map((staff) =>
            String(staff.email)
              .trim()
              .toLowerCase()
          )
      )

      // ============================================================
      // EXISTING STAFF PHONES
      // ============================================================

      const existingStaffPhones = new Set(
        existingStaffs
          .filter((staff) => staff.phone)
          .map((staff) =>
            String(staff.phone)
              .trim()
          )
      )

      // ============================================================
      // TRACK NEW BRANCHES
      // ============================================================

      const newBranches = []

      let skippedCount = 0
      let invalidCount = 0

      // ============================================================
      // PROCESS EACH EXCEL ROW
      // ============================================================

      for (const row of excelData) {

        const branchData = {
          branch_name: String(
            row.branch_name || ''
          ).trim(),

          address: String(
            row.address || ''
          ).trim(),

          phone: String(
            row.phone || ''
          ).trim(),

          email: String(
            row.email || ''
          ).trim().toLowerCase()
        }

        // ============================================================
        // SKIP COMPLETELY EMPTY ROW
        // ============================================================

        if (
          !branchData.branch_name &&
          !branchData.address &&
          !branchData.phone &&
          !branchData.email
        ) {
          continue
        }

        // ============================================================
        // VALIDATE EXCEL ROW
        // ============================================================

        const rowErrors = validateForm(branchData)

        if (Object.keys(rowErrors).length > 0) {
          invalidCount++
          continue
        }

        // ============================================================
        // CHECK BRANCH NAME
        // ============================================================

        const branchExists =
          existingBranchNames.has(
            branchData.branch_name
              .toLowerCase()
          )

        // ============================================================
        // CHECK EMAIL
        // Branch + Admin + Staff
        // ============================================================

        const branchEmailExists =
          branchData.email &&
          existingEmails.has(
            branchData.email
          )

        const adminEmailExists =
          branchData.email &&
          existingAdminEmails.has(
            branchData.email
          )

        const staffEmailExists =
          branchData.email &&
          existingStaffEmails.has(
            branchData.email
          )

        // ============================================================
        // CHECK PHONE
        // Branch + Admin + Staff
        // ============================================================

        const branchPhoneExists =
          branchData.phone &&
          existingPhones.has(
            branchData.phone
          )

        const adminPhoneExists =
          branchData.phone &&
          existingAdminPhones.has(
            branchData.phone
          )

        const staffPhoneExists =
          branchData.phone &&
          existingStaffPhones.has(
            branchData.phone
          )

        // ============================================================
        // IF EXISTING DATA FOUND
        // ============================================================

        if (
          branchExists ||
          branchEmailExists ||
          adminEmailExists ||
          staffEmailExists ||
          branchPhoneExists ||
          adminPhoneExists ||
          staffPhoneExists
        ) {
          skippedCount++
          continue
        }

        // ============================================================
        // CHECK DUPLICATE INSIDE CURRENT EXCEL UPLOAD
        // ============================================================

        const duplicateInCurrentUpload =
          newBranches.some((branch) => {

            const sameBranchName =
              branch.branch_name
                .trim()
                .toLowerCase() ===
              branchData.branch_name
                .trim()
                .toLowerCase()

            const sameEmail =
              branchData.email &&
              branch.email &&
              branch.email
                .trim()
                .toLowerCase() ===
              branchData.email
                .trim()
                .toLowerCase()

            const samePhone =
              branchData.phone &&
              branch.phone &&
              branch.phone.trim() ===
              branchData.phone.trim()

            return (
              sameBranchName ||
              sameEmail ||
              samePhone
            )
          })

        if (duplicateInCurrentUpload) {
          skippedCount++
          continue
        }

        // ============================================================
        // ADD NEW BRANCH
        // ============================================================

        newBranches.push(branchData)

        // ============================================================
        // UPDATE SETS
        // This prevents duplicate rows later in same Excel file
        // ============================================================

        existingBranchNames.add(
          branchData.branch_name
            .toLowerCase()
        )

        if (branchData.email) {
          existingEmails.add(
            branchData.email
          )
        }

        if (branchData.phone) {
          existingPhones.add(
            branchData.phone
          )
        }
      }

      // ============================================================
      // NO NEW BRANCHES
      // ============================================================

      if (!newBranches.length) {

        toast.info(
          `No new branches found. ${skippedCount} existing/duplicate branch(es) skipped and ${invalidCount} invalid row(s) skipped.`
        )

        return
      }

      // ============================================================
      // CREATE NEW BRANCHES
      // ============================================================

      let successCount = 0
      let failedCount = 0

      for (const branch of newBranches) {

        try {

          const response = await axios.post(
            `${config.baseurl}create_branch/`,
            branch,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Token ${token}`
              }
            }
          )

          if (
            response.status === 200 ||
            response.status === 201
          ) {
            successCount++
          } else {
            failedCount++
          }

        } catch (error) {

          console.error(
            'Failed to create branch:',
            branch,
            error.response?.data || error
          )

          failedCount++
        }
      }

      // ============================================================
      // SHOW UPLOAD RESULT
      // ============================================================

      if (successCount > 0) {
        toast.success(
          `${successCount} new branch(es) added successfully`
        )
      }

      if (skippedCount > 0) {
        toast.info(
          `${skippedCount} existing/duplicate branch(es) skipped`
        )
      }

      if (invalidCount > 0) {
        toast.warning(
          `${invalidCount} invalid row(s) skipped`
        )
      }

      if (failedCount > 0) {
        toast.error(
          `${failedCount} branch(es) failed to save`
        )
      }

      // ============================================================
      // RESET UPLOAD
      // ============================================================

      setSelectedFile(null)
      setOpenUploadModal(false)

    } catch (error) {

      console.error(
        'Excel upload error:',
        error.response?.data || error
      )

      toast.error(
        error.response?.data?.message ||
        'Failed to process Excel file'
      )

    } finally {
      setUploading(false)
    }
  }

  // Submit Single Branch
  const handleSubmit = async (e) => {
    e.preventDefault()

    const errors = validateForm(formData)

    if (Object.keys(errors).length > 0) {
      setErrors(errors)

      const firstError = Object.values(errors)[0]
      toast.error(firstError)
      return
    }

    try {
      setLoading(true)

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`
      }

      // ============================================================
      // FETCH LATEST BRANCH + ADMIN + STAFF DATA
      // ============================================================

      const [
        branchesResponse,
        adminsResponse,
        staffsResponse
      ] = await Promise.all([
        axios.get(
          `${config.baseurl}get_all_branches/`,
          { headers }
        ),

        axios.get(
          `${config.baseurl}get_all_admins/`,
          { headers }
        ),

        axios.get(
          `${config.baseurl}get_all_staff/`,
          { headers }
        )
      ])

      const branches =
        branchesResponse.data?.data || []

      const admins =
        adminsResponse.data?.data || []

      const staffs =
        staffsResponse.data?.data || []

      const branchName =
        formData.branch_name.trim().toLowerCase()

      const email =
        formData.email.trim().toLowerCase()

      const phone =
        formData.phone.trim()

      // ============================================================
      // CHECK BRANCH NAME
      // ============================================================

      const branchNameExists = branches.some(
        (branch) =>
          String(branch.branch_name || '')
            .trim()
            .toLowerCase() === branchName
      )

      if (branchNameExists) {
        toast.error(
          'This name already exists as a Branch'
        )
        return
      }

      // ============================================================
      // CHECK EMAIL IN BRANCH
      // ============================================================

      const branchEmailExists = branches.some(
        (branch) =>
          String(branch.email || '')
            .trim()
            .toLowerCase() === email
      )

      if (branchEmailExists) {
        toast.error(
          'This email already exists in a Branch'
        )
        return
      }

      // ============================================================
      // CHECK EMAIL IN ADMIN
      // ============================================================

      const adminEmailExists = admins.some(
        (admin) =>
          String(admin.email || '')
            .trim()
            .toLowerCase() === email
      )

      if (adminEmailExists) {
        toast.error(
          'This email already exists for an Admin'
        )
        return
      }

      // ============================================================
      // CHECK EMAIL IN STAFF
      // ============================================================

      const staffEmailExists = staffs.some(
        (staff) =>
          String(staff.email || '')
            .trim()
            .toLowerCase() === email
      )

      if (staffEmailExists) {
        toast.error(
          'This email already exists for a Staff'
        )
        return
      }

      // ============================================================
      // CHECK PHONE IN BRANCH
      // ============================================================

      const branchPhoneExists = branches.some(
        (branch) =>
          String(branch.phone || '').trim() === phone
      )

      if (branchPhoneExists) {
        toast.error(
          'This phone number already exists in a Branch'
        )
        return
      }

      // ============================================================
      // CHECK PHONE IN ADMIN
      // ============================================================

      const adminPhoneExists = admins.some(
        (admin) =>
          String(admin.phone || '').trim() === phone
      )

      if (adminPhoneExists) {
        toast.error(
          'This phone number already exists for an Admin'
        )
        return
      }

      // ============================================================
      // CHECK PHONE IN STAFF
      // ============================================================

      const staffPhoneExists = staffs.some(
        (staff) =>
          String(staff.phone || '').trim() === phone
      )

      if (staffPhoneExists) {
        toast.error(
          'This phone number already exists for a Staff'
        )
        return
      }

      // ============================================================
      // CREATE BRANCH
      // ============================================================

      const payload = {
        branch_name: formData.branch_name.trim(),
        address: formData.address.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim()
      }

      const res = await axios.post(
        `${config.baseurl}create_branch/`,
        payload,
        { headers }
      )

      if (res.status === 200 || res.status === 201) {
        toast.success(
          res.data?.message ||
          'Branch created successfully'
        )

        setFormData({
          branch_name: '',
          address: '',
          email: '',
          phone: ''
        })

        setErrors({})

        setTimeout(() => {
          navigate(-1)
        }, 300)
      } else {
        toast.error(
          res.data?.message ||
          'Failed to create branch'
        )
      }

    } catch (error) {

      console.error(
        'Error:',
        error.response?.data || error
      )

      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to create branch'

      toast.error(message)

    } finally {
      setLoading(false)
    }
  }

  // Close Upload Modal
  const closeUploadModal = () => {
    if (uploading) {
      return
    }

    setSelectedFile(null)
    setOpenUploadModal(false)
  }

  // Reusable Input Class
  const inputClass ='w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 placeholder:text-slate-400'

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 ml-64 5xl:ml-0">
      <SuperAdminHeader />

      {/* HEADER */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-6">
        <div className="items-center justify-between text-center">
          <h1 className="mt-1 text-3xl font-bold text-slate-800">
            ADD BRANCH
          </h1>

          <p className="mt-2 text-sm text-slate-500">
            Create a new branch or add multiple branches using an Excel file.
          </p>
        </div>
      </div>

      {/* FORM SECTION */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className='mx-auto max-w-5xl mb-4 flex justify-end items-center gap-3'>
        
        {/* Excel Actions */}
          <button
            type="button"
            onClick={handleExcel}
            className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
          >
            Download Excel File
          </button>

          <button
            type="button"
            onClick={() =>setOpenUploadModal(true)}
            className="inline-flex items-center justify-center rounded-xl bg-[#000435] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#000326]"
          >
            Upload Excel File
          </button>
        </div>
        
        <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">

          {/* Form Header */}
          <div className="border-b border-slate-200 bg-slate-50 px-6 py-5 text-center text-lg">
            <h2 className="text-lg font-bold text-slate-800">
              Branch Registration Form
            </h2>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

              {/* Branch Name */}
              <div className="md:col-span-2">
                <label
                  htmlFor="branch_name"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Branch Name
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <input
                  id="branch_name"
                  type="text"
                  name="branch_name"
                  placeholder="Enter branch name"
                  value={formData.branch_name}
                  onChange={handleChange}
                  className={`${inputClass} ${
                    errors.branch_name
                      ? 'border-red-500 focus:border-red-600'
                      : ''
                  }`}
                />

                {errors.branch_name && (
                  <p className="mt-1.5 text-xs font-medium text-red-500">
                    {errors.branch_name}
                  </p>
                )}
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label
                  htmlFor="address"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Address
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <textarea
                  id="address"
                  name="address"
                  rows={4}
                  placeholder="Enter complete branch address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="phone"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Phone Number
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <input
                  id="phone"
                  type="text"
                  name="phone"
                  placeholder="Enter 10-digit phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={10}
                  inputMode="numeric"
                  className={`${inputClass} ${errors.phone
                    ? 'border-red-500 focus:border-red-600'
                    : ''
                  }`}
                />

                {errors.phone && (
                  <p className='mt-1.5 text-xs font-medium text-red-500'>{errors.phone}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Email Address
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleChange}
                  className={`${inputClass} ${errors.email
                    ? 'border-red-500 focus:border-red-600'
                    : ''
                  }`}
                />

                {errors.email && (
                  <p className='mt-1.5 text-xs font-medium text-red-500'>{errors.email}</p>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 sm:flex-row justify-end">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={loading}
                className="rounded-xl bg-slate-100 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading || Object.keys(errors).some(key => errors[key])}
                className="rounded-xl bg-[#000435] px-7 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#000326] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit'} 
                {/* 7890615212, ernakulambranch@gmail.com	 */}
                {/* 8571926402. aureviatrust_ekm@gmail.com */}
                {/* jaijaithu@gmail.com	8571926430 */}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* EXCEL UPLOAD MODAL */}
      {openUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between bg-[#000435] px-6 py-5">
              <div>
                <h2 className="text-xl font-bold text-white">Upload Branches</h2>

                <p className="mt-1 text-sm text-slate-300">
                  Import new branches using an Excel file.
                </p>
              </div>

              <button
                type="button"
                onClick={closeUploadModal}
                disabled={uploading}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl text-red-500 hover:text-red-600 transition disabled:cursor-not-allowed disabled:opacity-50"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleUpload} className="p-6">

              {/* File Selection */}
              <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center transition hover:border-blue-300">
                <div className="mb-5">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
                    <span className="text-2xl">📊</span>
                  </div>

                  <p className="text-lg font-bold text-slate-800">
                    Select Excel File
                  </p>

                  <p className="mt-1 text-sm text-slate-500">
                    Supported formats: .xlsx and .xls
                  </p>
                </div>

                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-white file:border-r file:border-r-slate-300 file:px-4 file:py-2 file:font-semibold file:text-slate-700 file:shadow-sm hover:file:bg-blue-50 border border-slate-300 rounded-lg bg-slate-300"
                />

                {selectedFile && (
                  <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 text-left">
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                      Selected File
                    </p>

                    <p className="mt-1 break-all text-sm font-medium text-blue-900">
                      {selectedFile.name}
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  disabled={uploading}
                  className="rounded-xl bg-slate-100 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={ uploading || !selectedFile}
                  className="rounded-xl bg-[#000435] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#000326] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

